<template>
    <Headify :data="headMeta" />

    <div class="min-h-screen bg-white">
        <NavMain :key="route.name" />

        <div class="flex lg:pl-64">
            <main class="h-screen flex-1 overflow-auto focus:outline-hidden">
                <div class="mx-auto w-full max-w-full min-w-fit px-2 pt-12 sm:px-4 lg:pt-4">
                    <div class="py-2">
                        <Breadcrumbs :links="breadcrumbsLinks" />
                    </div>

                    <div class="icon-stack my-4 flex">
                        <button
                            v-tippy="t('imageEditorOpenFile')"
                            class="flex-none rounded p-1 text-sm text-gray-400 hover:bg-gray-200 hover:text-gray-700"
                            @click="chooseFile()"
                        >
                            <div class="dropzone-file">
                                <MdiOpenInApp class="h-6 w-6" />
                                <input
                                    id="dropzone-file"
                                    type="file"
                                    class="hidden"
                                    accept="image/png,image/jpg,image/jpeg"
                                    @change="dropFileFromInput(dataPack)"
                                />
                            </div>
                        </button>
                        <button
                            v-tippy="t('imageEditorRotateLeft')"
                            :class="[
                                validResult()
                                    ? 'text-gray-400 hover:bg-gray-200 hover:text-gray-700'
                                    : 'text-gray-300',
                                'flex-none rounded p-1 text-sm',
                            ]"
                            :disabled="!validResult()"
                            @click="rotate(dataPack, -90)"
                        >
                            <MdiRotateLeft class="h-6 w-6" />
                        </button>
                        <button
                            v-tippy="t('imageEditorRotateRight')"
                            :class="[
                                validResult()
                                    ? 'text-gray-400 hover:bg-gray-200 hover:text-gray-700'
                                    : 'text-gray-300',
                                'flex-none rounded p-1 text-sm',
                            ]"
                            :disabled="!validResult()"
                            @click="rotate(dataPack, 90)"
                        >
                            <MdiRotateRight class="h-6 w-6" />
                        </button>
                        <button
                            v-tippy="t('imageEditorFlipH')"
                            :class="[
                                validResult()
                                    ? 'text-gray-400 hover:bg-gray-200 hover:text-gray-700'
                                    : 'text-gray-300',
                                'flex-none rounded p-1 text-sm',
                            ]"
                            :disabled="!validResult()"
                            @click="flipH(dataPack)"
                        >
                            <MdiFlipHorizontal class="h-6 w-6" />
                        </button>
                        <button
                            v-tippy="t('imageEditorFlipV')"
                            :class="[
                                validResult()
                                    ? 'text-gray-400 hover:bg-gray-200 hover:text-gray-700'
                                    : 'text-gray-300',
                                'flex-none rounded p-1 text-sm',
                            ]"
                            :disabled="!validResult()"
                            @click="flipV(dataPack)"
                        >
                            <MdiFlipVertical class="h-6 w-6" />
                        </button>
                        <button
                            v-tippy="t('imageEditorZoomIn')"
                            :class="[
                                validResult()
                                    ? 'text-gray-400 hover:bg-gray-200 hover:text-gray-700'
                                    : 'text-gray-300',
                                'flex-none rounded p-1 text-sm',
                            ]"
                            :disabled="!validResult()"
                            @click="zoom(dataPack, 0.1)"
                        >
                            <MdiMagnifyPlus class="h-6 w-6" />
                        </button>
                        <button
                            v-tippy="t('imageEditorZoomOut')"
                            :class="[
                                validResult()
                                    ? 'text-gray-400 hover:bg-gray-200 hover:text-gray-700'
                                    : 'text-gray-300',
                                'flex-none rounded p-1 text-sm',
                            ]"
                            :disabled="!validResult()"
                            @click="zoom(dataPack, -0.1)"
                        >
                            <MdiMagnifyMinus class="h-6 w-6" />
                        </button>
                        <button
                            v-tippy="t('imageEditorMoveLeft')"
                            :class="[
                                validResult()
                                    ? 'text-gray-400 hover:bg-gray-200 hover:text-gray-700'
                                    : 'text-gray-300',
                                'flex-none rounded p-1 text-sm',
                            ]"
                            :disabled="!validResult()"
                            @click="move(dataPack, -10, 0)"
                        >
                            <MdiArrowLeft class="h-6 w-6" />
                        </button>
                        <button
                            v-tippy="t('imageEditorMoveRight')"
                            :class="[
                                validResult()
                                    ? 'text-gray-400 hover:bg-gray-200 hover:text-gray-700'
                                    : 'text-gray-300',
                                'flex-none rounded p-1 text-sm',
                            ]"
                            :disabled="!validResult()"
                            @click="move(dataPack, 10, 0)"
                        >
                            <MdiArrowRight class="h-6 w-6" />
                        </button>
                        <button
                            v-tippy="t('imageEditorMoveUp')"
                            :class="[
                                validResult()
                                    ? 'text-gray-400 hover:bg-gray-200 hover:text-gray-700'
                                    : 'text-gray-300',
                                'flex-none rounded p-1 text-sm',
                            ]"
                            :disabled="!validResult()"
                            @click="move(dataPack, 0, -10)"
                        >
                            <MdiArrowUp class="h-6 w-6" />
                        </button>
                        <button
                            v-tippy="t('imageEditorMoveDown')"
                            :class="[
                                validResult()
                                    ? 'text-gray-400 hover:bg-gray-200 hover:text-gray-700'
                                    : 'text-gray-300',
                                'flex-none rounded p-1 text-sm',
                            ]"
                            :disabled="!validResult()"
                            @click="move(dataPack, 0, 10)"
                        >
                            <MdiArrowDown class="h-6 w-6" />
                        </button>
                        <button
                            v-tippy="t('imageEditorRevert')"
                            :class="[
                                validResult()
                                    ? 'text-red-400 hover:bg-red-200 hover:text-red-700'
                                    : 'text-red-300',
                                'flex flex-none items-center justify-center gap-2 rounded-sm px-2 py-1 text-sm',
                            ]"
                            :disabled="!validResult()"
                            @click="reset(dataPack)"
                        >
                            <MdiLockReset class="h-6 w-6" />
                            <span>{{ t('imageEditorRevert') }}</span>
                        </button>
                        <button
                            v-tippy="t('imageEditorCrop')"
                            :class="[
                                validResult()
                                    ? 'text-emerald-400 hover:bg-emerald-200 hover:text-emerald-700'
                                    : 'text-emerald-300',
                                'flex flex-none items-center justify-center gap-2 rounded-sm px-2 py-1 text-sm',
                            ]"
                            :disabled="!validResult()"
                            @click="ask(dataPack)"
                        >
                            <MdiCrop class="h-6 w-6" />
                            <span>{{ t('imageEditorCrop') }}</span>
                        </button>
                    </div>

                    <div
                        class="mt-4 grid grid-cols-12 gap-4 border border-dashed border-gray-300 p-4"
                    >
                        <div
                            id="editor-wrapper"
                            class="col-span-10 flex min-h-[200px]"
                        >
                            <vue-cropper
                                v-if="validResult()"
                                ref="cropper"
                                :src="dataPack.image"
                                alt="Source Image"
                                preview=".preview"
                                :responsive="dataPack.responsive"
                                :zoom-on-wheel="dataPack.zoomOnWheel"
                            >
                            </vue-cropper>
                            <div
                                v-if="!validResult()"
                                class="flex w-full cursor-pointer items-center justify-center text-gray-200 hover:text-gray-300"
                                @click="chooseFile()"
                            >
                                <MdiCloudUpload class="h-24 w-24" />
                            </div>
                        </div>
                        <div class="preview-area col-span-2">
                            <div class="preview"></div>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    </div>

    <FileEditorResult
        :state="fileResultModal.state"
        :buttons="fileResultModal.buttons"
    />

    <Spinner
        v-if="saving.active"
        type="dots"
        :color="`text-blue-600`"
        :color2="`text-blue-400`"
        :text-color="`text-gray-200`"
        :overlay="true"
        :speed="1000"
        :dots="true"
        :dot-char="'.'"
        :dot-dual="false"
        :dot-reverse="false"
        :text="saving.text"
        :inline="false"
        :pulse="true"
    />
</template>
<script setup>
import { reactive, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import Headify from '../components/Headify.vue';
import Spinner from '../ui/Spinner.vue';
import Breadcrumbs from '../ui/Breadcrumbs.vue';
import NavMain from '../ui/NavMain.vue';
import FileEditorResult from '../components/FileEditorResult.vue';
import navs from '../composables/navs';
import { t } from '../composables/i18n';
import {
    dropFileFromInput,
    crop,
    flipH,
    flipV,
    move,
    rotate,
    zoom,
    reset,
    resize,
} from '../composables/useImage';
import { upload, dataUrlToBinaryFile } from '../composables/useFiles';
import slugify from 'slugify';
import { getTemplate } from '../composables/useTemplates';
import VueCropper from 'vue-cropperjs';
import 'cropperjs/dist/cropper.css';

import {
    MdiCrop,
    MdiOpenInApp,
    MdiRotateLeft,
    MdiRotateRight,
    MdiLockReset,
    MdiFlipVertical,
    MdiFlipHorizontal,
    MdiMagnifyPlus,
    MdiMagnifyMinus,
    MdiArrowLeft,
    MdiArrowRight,
    MdiArrowUp,
    MdiArrowDown,
    MdiCloudUpload,
    MdiChevronLeft,
    MdiContentSave,
} from 'materialdesignicons-vue3/icons/';
import { asleep, keeper } from '../composables/utils';

const route = useRoute();
const router = useRouter();
let dirname = route.params.dirname || '';
// TO-DO: Edit existing image
// TO-DO: Preloader on modal and page
//let filename = route.params.filename || '';
const template = (await getTemplate(dirname.split('-').pop())) || [];

const breadcrumbsLinks = [
    {
        name: 'dashboard',
        title: t('Dashboard'),
    },
    {
        name: 'files',
        title: t('Files'),
    },
    {
        name: 'directory',
        title: template.name,
        params: {
            dirname: dirname,
        },
    },
    {
        title: t('File'),
        final: true,
    },
];

const saving = reactive({
    active: false,
    text: t('loading'),
});

const headMeta = navs().metaFind(route.name);
const cropper = ref(false);

const dataPack = reactive({
    cropper: cropper,
    image: '',
    name: '',
    temp: '',
    type: '',
    size: {},
    flipX: 1,
    flipY: 1,
    result: '',
    responsive: true,
    zoomOnWheel: false,
});

const fileResultModal = reactive({
    state: {
        open: false,
        title: t('imageEditorPreview'),
        text: t('imageEditorPreviewText'),
        maxWidthMax: true,
        dataPack: dataPack,
        ready: {
            update: false,
        },
        overlayPreventClose: true,
    },
    buttons: [
        {
            title: t('buttonCancel'),
            action: () => {
                fileResultModal.state.open = false;
            },
            state: 'neutral',
            icon: MdiChevronLeft,
        },
        {
            title: t('buttonSave'),
            action: async () => {
                await dataUrlToBinaryFile(
                    dataPack.result,
                    slugify(dataPack.name.toLocaleLowerCase()),
                    dataPack.tyoe,
                ).then(function (file) {
                    upload(dirname, file);
                });
                fileResultModal.state.open = false;
                await asleep(400);
                router.push({
                    name: 'directory',
                    params: {
                        dirname: dirname,
                    },
                });
            },
            state: 'primary',
            icon: MdiContentSave,
        },
    ],
});

const ask = async (dataPack) => {
    saving.active = true;
    dataPack.temp = dataPack.name;
    await asleep(200);
    crop(dataPack);
    await asleep(200);
    await resize(dataPack, keeper('imageMaxWidth'), keeper('imageMaxHeight'));
    await asleep(200);
    saving.active = false;
    fileResultModal.state.open = true;
    fileResultModal.state.ready.update = true;
};

const chooseFile = function () {
    document.querySelector('#dropzone-file').click();
};

const validResult = () => {
    return typeof dataPack.result === 'string' && dataPack.result.length > 10;
};
</script>
<style lang="scss">
.preview {
    width: 100%;
    height: 100%;
    max-height: 200px;
    overflow: hidden;
}
</style>
